import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Container,
    Paper,
    Text,
    SimpleGrid,
    Stack,
    createStyles,
    Group,
    Modal,
    Button,
    Textarea,
    Alert,
    Badge,
    Radio,
    Select,
    Divider,
    ActionIcon,
    ThemeIcon,
    SegmentedControl,
    Checkbox,
    Box,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import {
    IconFirstAidKit,
    IconCurrencyDollarOff,
    IconBeach,
    IconAlertCircle,
    IconCheck,
    IconCalendarEvent,
    IconX,
    IconInfoCircle,
    IconQuestionMark,
    IconCalendar,
    IconArrowLeft,
    IconArrowRight,
} from '@tabler/icons-react';
import { collection, serverTimestamp as firestoreServerTimestamp, getDocs, query, where, doc, getDoc, writeBatch, increment, collectionGroup, setDoc, limit, startAfter, orderBy, getCountFromServer } from 'firebase/firestore';
import { db } from '../../firebase/FirebaseCredential';
import { useAppSelector } from '../../hooks/hooks';
import { RootState } from '../../store/store';
import BenchBreadcrumbs from '../UIComponent/Components/BenchBreadcrumbs';
import BenchLoader from '../UIComponent/BenchLoader/BenchLoader';
import { ROUTES } from '../../routes/routesPaths';

// Types and Constants
import { LeaveType, LeaveRecord, LeaveData, LeaveTypeConfig, HalfDayType, DayDetail } from '../../types/leave.types';
import {
    LEAVE_TYPES_CONFIG,
    MAX_LEAVE_DURATION,
} from '../../constants/leave.constants';
import {
    calculateDuration,
    getTodayMidnight,
    normalizeDateToMidnight,
    getTomorrowMidnight,
    formatDateToISO,
    formatDate
} from '../../utils/leave.utils';

// Components
import { LeaveSummaryCard } from './components/LeaveSummaryCard';
import { LeaveTable } from './components/LeaveTable';
import { LeaveTypeSelector } from './components/LeaveTypeSelector';
import { showToast } from '../Utils/toastHelper';
import BenchSettingsBreadcrumbs from '../UIComponent/Components/BenchSettingsBreadcrumbs';
import { toast } from 'react-toastify';
import PermissionButton from '../PermissionComponent/PermissionButton';
import BenchPaper from '../pages/UI/BenchPaper';

// Styles
const useStyles = createStyles((theme) => ({
    casualIcon: {
        backgroundColor: '#e7f5ff',
    },
    lopIcon: {
        backgroundColor: '#ffe9e9',
    },
    sickIcon: {
        backgroundColor: '#e6fcf5',
    },
    modalHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingBottom: theme.spacing.md,
    },
    closeButton: {
        color: '#ee374d',
        backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[6] : theme.colors.gray[0],
        '&:hover': {
            backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[5] : theme.colors.gray[1],
        },
    },
    segmentedControl: {
        backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[6] : theme.colors.gray[0],
        borderRadius: theme.radius.md,
        padding: '4px',
        display: 'flex',
        gap: '4px',
    },
    segmentedButton: {
        flex: 1,
        textAlign: 'center',
        padding: '8px 12px',
        borderRadius: theme.radius.sm,
        fontSize: theme.fontSizes.sm,
        fontWeight: 500,
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        color: theme.colorScheme === 'dark' ? theme.colors.dark[2] : theme.colors.gray[6],
        '&:hover': {
            backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[5] : theme.colors.gray[1],
        },
        '&[data-active="true"]': {
            backgroundColor: '#ee374d',
            color: theme.white,
        },
    },
    infoBox: {
        backgroundColor: theme.colorScheme === 'dark' ? 'rgba(238, 55, 77, 0.05)' : '#fff5f5',
        borderLeft: '4px solid #ee374d',
        padding: theme.spacing.md,
        borderRadius: theme.radius.sm,
    },
    labelWithIcon: {
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        marginBottom: '4px',
    },
    inputActive: {
        border: '1px solid #ee374d !important',
        boxShadow: '0 0 0 2px rgba(238, 55, 77, 0.1) !important',
    },
    submitButton: {
        backgroundColor: '#ee374d',
        '&:hover': {
            backgroundColor: '#d62e43',
        },
    },
    cancelButton: {
        backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[6] : theme.colors.gray[0],
        color: theme.colorScheme === 'dark' ? theme.colors.dark[0] : theme.colors.gray[7],
        '&:hover': {
            backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[5] : theme.colors.gray[1],
        },
    },
}));

export default function LeaveSummaryPage() {
    const { classes } = useStyles();
    const navigate = useNavigate();
    const companyId = useAppSelector((state: RootState) => state.session.company_id);
    const userId = useAppSelector((state: RootState) => state.session.uid);
    const userName = useAppSelector((state: RootState) =>
        `${state.session.first_name || ''} ${state.session.last_name || ''}`.trim()
    );
    const mappedBranchIds = useAppSelector((state: RootState) => state.session.mapped_branch_id);


    // Modal state
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedLeaveType, setSelectedLeaveType] = useState<LeaveType>('Casual');

    // Form state
    const [startDate, setStartDate] = useState<Date | null>(null);
    const [endDate, setEndDate] = useState<Date | null>(null);
    const [dayConfigurations, setDayConfigurations] = useState<Map<string, { isHalfDay: boolean; halfDayType: HalfDayType }>>(new Map());
    const [reason, setReason] = useState('');
    const [remarks, setRemarks] = useState('');
    const [documentation, setDocumentation] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Leave records state
    const [leaveRecords, setLeaveRecords] = useState<LeaveRecord[]>([]);
    const [fetchLoading, setFetchLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);

    // Leave balance state
    const [leaveBalance, setLeaveBalance] = useState<{
        casual_leave_balance: number;
        casual_leave_booked: number;
        sick_leave_balance: number;
        sick_leave_booked: number;
        lop_leave_balance: number;
        lop_leave_booked: number;
    } | null>(null);
    const [workingDays, setWorkingDays] = useState<string[]>([]);
    const [isStaffAttendanceEnabled, setIsStaffAttendanceEnabled] = useState<boolean>(false);
    const [holidayDates, setHolidayDates] = useState<string[]>([]);

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState<string>("10");
    const [pageCursors, setPageCursors] = useState<any[]>([]);
    const [hasNextPage, setHasNextPage] = useState(false);
    const [totalResultCount, setTotalResultCount] = useState(0);

    // Memoized selected config
    const selectedLeaveConfig = useMemo(() => {
        return LEAVE_TYPES_CONFIG.find(config => config.type === selectedLeaveType) || LEAVE_TYPES_CONFIG[0];
    }, [selectedLeaveType]);

    // Memoized breadcrumb links
    const breadcrumbLinks = useMemo(() => [
        { label: 'Leave Management' }
    ], []);

    // Fetch leave balance callback
    const fetchLeaveBalance = useCallback(async () => {
        if (!companyId || !userId) {
            return;
        }

        try {
            const adminRef = collection(db, "admin");
            const q = query(
                adminRef,
                where("uid", "==", userId),
                where("company_id", "==", companyId)
            );

            const querySnapshot = await getDocs(q);

            if (querySnapshot.empty) {
                console.log("No matching admin document found.");
                return;
            }
            const adminDoc = querySnapshot.docs[0];
            const adminId = adminDoc.id;
            const year = new Date().getFullYear().toString();

            const leaveBalanceRef = doc(db, 'admin', adminId, 'leavebalance', year);
            const leaveBalanceSnapshot = await getDoc(leaveBalanceRef);

            if (leaveBalanceSnapshot.exists()) {
                const data = leaveBalanceSnapshot.data();
                setLeaveBalance({
                    casual_leave_balance: data?.casual_leave_balance || 0,
                    casual_leave_booked: data?.casual_leave_booked || 0,
                    sick_leave_balance: data?.sick_leave_balance || 0,
                    sick_leave_booked: data?.sick_leave_booked || 0,
                    lop_leave_balance: data?.lop_leave_balance || 0,
                    lop_leave_booked: data?.lop_leave_booked || 0,
                });
                // console.log('Leave balance fetched:', data);
            } else {
                console.log('No leave balance document found for ' + year + ', creating it...');
                const newBalance = {
                    admin_id: adminId,
                    company_id: companyId,
                    year: parseInt(year),
                    casual_leave_balance: 0,
                    casual_leave_booked: 0,
                    sick_leave_balance: 0,
                    sick_leave_booked: 0,
                    lop_leave_balance: 0,
                    lop_leave_booked: 0,
                    created_at: firestoreServerTimestamp(),
                };
                await setDoc(leaveBalanceRef, newBalance);
                setLeaveBalance({
                    casual_leave_balance: 0,
                    casual_leave_booked: 0,
                    sick_leave_balance: 0,
                    sick_leave_booked: 0,
                    lop_leave_balance: 0,
                    lop_leave_booked: 0,
                });
            }
        } catch (err) {
            console.error('Error fetching leave balance:', err);

        }
    }, [companyId, userId]);

    // Fetch leave records callback
    const fetchLeaveRecords = useCallback(async (direction: "next" | "prev" | "first" = "first", newItemsPerPage?: string) => {
        if (!companyId || !userId) {
            setFetchLoading(false);
            return;
        }

        try {
            setFetchLoading(true);
            const perPage = parseInt(newItemsPerPage || itemsPerPage);

            // First, get the admin document to find the adminId
            const adminRef = collection(db, "admin");
            const adminQuery = query(
                adminRef,
                where("uid", "==", userId),
                where("company_id", "==", companyId)
            );
            const adminSnapshot = await getDocs(adminQuery);

            if (adminSnapshot.empty) {
                console.log("No matching admin document found.");
                setFetchLoading(false);
                return;
            }

            const adminId = adminSnapshot.docs[0].id;
            const leaveRef = collection(db, 'admin', adminId, 'leaverequests');

            // Get count (optional, but good for total count display if needed, though mostly for validation here)
            const countQ = query(leaveRef); // You might want to cache this or optimize if too many docs
            const countSnap = await getCountFromServer(countQ);
            setTotalResultCount(countSnap.data().count);

            let q = query(
                leaveRef,
                orderBy('leave_date', 'desc'),
                limit(perPage + 1)
            );

            if (direction === "next" && pageCursors.length > 0) {
                q = query(q, startAfter(pageCursors[pageCursors.length - 1]));
            } else if (direction === "prev" && currentPage > 1) {
                const targetCursorIndex = currentPage - 3;
                if (targetCursorIndex >= 0) {
                    q = query(q, startAfter(pageCursors[targetCursorIndex]));
                }
            }

            const querySnapshot = await getDocs(q);
            const docs = querySnapshot.docs;
            const hasNext = docs.length > perPage;
            setHasNextPage(hasNext);

            const visibleDocs = hasNext ? docs.slice(0, perPage) : docs;

            const records: LeaveRecord[] = [];
            visibleDocs.forEach((doc) => {
                const data = doc.data() as LeaveData;

                // Duration from fh/sh status
                let duration = 0;
                if (data.fh_status) duration += 0.5;
                if (data.sh_status) duration += 0.5;

                records.push({
                    id: doc.id,
                    ...data,
                    duration,
                    employee_name: userName
                } as LeaveRecord);
            });

            // records.sort is removed because we used orderBy('leave_date', 'desc')
            console.log('Leave records fetched:', records);

            setLeaveRecords(records);

            if (direction === "next") {
                setPageCursors((prev) => [...prev, visibleDocs[visibleDocs.length - 1]]);
                setCurrentPage((prev) => prev + 1);
            } else if (direction === "prev") {
                setPageCursors((prev) => prev.slice(0, prev.length - 1));
                setCurrentPage((prev) => prev - 1);
            } else {
                setPageCursors([visibleDocs[visibleDocs.length - 1]]);
                setCurrentPage(1);
            }

        } catch (err) {
            console.error('Error fetching leave records:', err);

        } finally {
            setFetchLoading(false);
        }
    }, [companyId, userId, userName, itemsPerPage, pageCursors, currentPage]);

    // Fetch leave balance and records on mount
    const fetchBusinessSettings = useCallback(async () => {
        if (!companyId) {
            console.log('[Leave Debug] fetchBusinessSettings skipped - no companyId');
            return;
        }

        console.log('[Leave Debug] fetchBusinessSettings starting with:', { companyId, mappedBranchIds });

        // 1. Fetch Attendance Settings
        try {
            const companyDocRef = doc(db, "companyprofile", String(companyId));
            const companySettingsRef = collection(companyDocRef, "companysettings");
            const attendanceSettingsRef = doc(companySettingsRef, "attendancesettings");
            const attendanceSnap = await getDoc(attendanceSettingsRef);

            if (attendanceSnap.exists()) {
                const staffAttEnabled = attendanceSnap.data().staff_attendance_enabled || false;
                console.log('[Leave Debug] Company staff_attendance_enabled:', staffAttEnabled);
                setIsStaffAttendanceEnabled(staffAttEnabled);
            } else {
                console.log('[Leave Debug] Company attendancesettings doc does NOT exist');
                // Try branch level as fallback for staff attendance setting if company-level is missing
                if (mappedBranchIds && mappedBranchIds.length >= 1) {
                    const branchId = mappedBranchIds[0];
                    const branchAttendanceRef = doc(db, "branch", branchId, "branchsettings", "attendancesettings");
                    const branchAttendanceSnap = await getDoc(branchAttendanceRef);
                    if (branchAttendanceSnap.exists()) {
                        const branchStaffAttEnabled = branchAttendanceSnap.data().staff_attendance_enabled || false;
                        console.log('[Leave Debug] Branch-level staff_attendance_enabled:', branchStaffAttEnabled);
                        setIsStaffAttendanceEnabled(branchStaffAttEnabled);
                    }
                }
            }
        } catch (error) {
            console.error('[Leave Debug] Error fetching company attendance settings:', error);
            // Even if company-level fails (permissions?), we might still want to check branch-level
            if (mappedBranchIds && mappedBranchIds.length >= 1) {
                try {
                    const branchId = mappedBranchIds[0];
                    const branchAttendanceRef = doc(db, "branch", branchId, "branchsettings", "attendancesettings");
                    const branchAttendanceSnap = await getDoc(branchAttendanceRef);
                    if (branchAttendanceSnap.exists()) {
                        const branchStaffAttEnabled = branchAttendanceSnap.data().staff_attendance_enabled || false;
                        console.log('[Leave Debug] Branch-level staff_attendance_enabled after company error:', branchStaffAttEnabled);
                        setIsStaffAttendanceEnabled(branchStaffAttEnabled);
                    }
                } catch (branchError) {
                    console.error('[Leave Debug] Error fetching branch attendance settings:', branchError);
                }
            }
        }

        // 2. Fetch Business Hours Hierarchically
        try {
            let foundBranchHours = false;
            // Iterate all mapped branches to find the first one with configured business hours
            if (mappedBranchIds && mappedBranchIds.length >= 1) {
                for (const branchId of mappedBranchIds) {
                    try {
                        const branchBusinessHoursRef = doc(db, "branch", branchId, "branchsettings", "businesshours");
                        const branchSnap = await getDoc(branchBusinessHoursRef);
                        if (branchSnap.exists()) {
                            const selectedDays = branchSnap.data().selectedDays || [];
                            if (selectedDays.length > 0) {
                                setWorkingDays(selectedDays);
                                foundBranchHours = true;
                                console.log('[Leave Debug] Using branch-level business hours for branch:', branchId);
                                break;
                            }
                        }
                    } catch (err) {
                        console.warn(`[Leave Debug] Could not read hours for branch ${branchId}:`, err);
                    }
                }
            }

            if (!foundBranchHours) {
                console.log('[Leave Debug] Falling back to company-level business hours');
                try {
                    const companyDocRef = doc(db, "companyprofile", String(companyId));
                    const companySettingsRef = collection(companyDocRef, "companysettings");
                    const businessHoursRef = doc(companySettingsRef, "businesshours");
                    const businessHoursSnap = await getDoc(businessHoursRef);
                    if (businessHoursSnap.exists()) {
                        const companyDays = businessHoursSnap.data().selectedDays || [];
                        console.log('[Leave Debug] Company-level selectedDays:', companyDays);
                        setWorkingDays(companyDays);
                    } else {
                        console.log('[Leave Debug] Company businesshours doc does NOT exist');
                    }
                } catch (companyErr) {
                    console.error('[Leave Debug] Error fetching company business hours:', companyErr);
                }
            }
        } catch (error) {
            console.error('[Leave Debug] General error in business hours logic:', error);
        }

        // 3. Fetch Holidays
        try {
            const currentYear = new Date().getFullYear();
            const companyDocRef = doc(db, "companyprofile", String(companyId));
            const holidayListRef = collection(companyDocRef, "holidaylist");
            const holidayQuery = query(
                holidayListRef,
                where("year", "==", currentYear),
                where("company_id", "==", companyId)
            );
            const holidaySnapshot = await getDocs(holidayQuery);

            if (!holidaySnapshot.empty) {
                const allHolidayDates: string[] = [];
                holidaySnapshot.forEach(doc => {
                    const data = doc.data();
                    const holidayList = data.holiday_list || [];
                    holidayList.forEach((h: any) => {
                        if (h.date) {
                            const d = h.date?.toDate?.() || new Date(h.date);
                            allHolidayDates.push(formatDateToISO(d));
                        }
                    });
                });
                console.log('[Leave Debug] Fetched holiday dates count:', allHolidayDates.length);
                setHolidayDates(allHolidayDates);
            } else {
                console.log('[Leave Debug] No holidays found for current year');
            }
        } catch (error) {
            console.error('[Leave Debug] Error fetching holidays:', error);
        }
    }, [companyId, mappedBranchIds]);


    useEffect(() => {
        // Reset states when user/company changes
        setLeaveRecords([]);
        setCurrentPage(1);
        setPageCursors([]);
        setInitialLoading(true);

        const init = async () => {
            try {
                await Promise.all([
                    fetchLeaveBalance(),
                    fetchBusinessSettings(),
                    fetchLeaveRecords('first')
                ]);
            } finally {
                setInitialLoading(false);
            }
        };

        if (companyId && userId) {
            init();
        }
    }, [companyId, userId]); // Only run when user/company changes. fetch* functions are excluded to prevent loop.

    useEffect(() => {
        if (startDate && endDate) {
            const newConfigs = new Map(dayConfigurations);
            const current = new Date(startDate);
            const end = new Date(endDate);

            // Add dates that are new in range
            const rangeDates = new Set<string>();
            const temp = new Date(startDate);
            while (temp <= end) {
                rangeDates.add(formatDateToISO(temp));
                temp.setDate(temp.getDate() + 1);
            }

            // Remove dates no longer in range
            for (const key of Array.from(newConfigs.keys())) {
                if (!rangeDates.has(key)) {
                    newConfigs.delete(key);
                }
            }

            // Ensure all range dates have configs
            while (current <= end) {
                const dateKey = formatDateToISO(current);
                if (!newConfigs.has(dateKey)) {
                    newConfigs.set(dateKey, { isHalfDay: false, halfDayType: 'first_half' });
                }
                current.setDate(current.getDate() + 1);
            }

            // Only update if something changed
            if (newConfigs.size !== dayConfigurations.size ||
                Array.from(newConfigs.keys()).some(k => !dayConfigurations.has(k))) {
                setDayConfigurations(newConfigs);
            }
        } else if (dayConfigurations.size > 0) {
            setDayConfigurations(new Map());
        }
    }, [startDate, endDate]);

    // Memoized duration calculation based on day configurations
    const duration = useMemo(() => {
        if (!startDate || !endDate) return 0;

        let totalDays = 0;
        const current = new Date(startDate);
        const end = new Date(endDate);

        while (current <= end) {
            const dateKey = formatDateToISO(current);
            const config = dayConfigurations.get(dateKey);
            totalDays += config?.isHalfDay ? 0.5 : 1;
            current.setDate(current.getDate() + 1);
        }

        return totalDays;
    }, [startDate, endDate, dayConfigurations]);

    const resetForm = useCallback(() => {
        setStartDate(null);
        setEndDate(null);
        setDayConfigurations(new Map());
        setReason('');
        setRemarks('');
        setDocumentation('');
        setError(null);
    }, []);

    const handleOpenModal = useCallback((leaveType?: LeaveType) => {
        if (leaveType) {
            setSelectedLeaveType(leaveType);
        }
        setModalOpen(true);
        resetForm();
    }, [resetForm]);

    const handleCloseModal = useCallback(() => {
        setModalOpen(false);
        resetForm();
    }, [resetForm]);

    // Memoized date constraints
    const getMinDate = useCallback((): Date => {
        const today = getTodayMidnight();

        // Add advance notice days if specified
        if (selectedLeaveConfig.advanceNotice && selectedLeaveConfig.advanceNotice > 0) {
            today.setDate(today.getDate() + selectedLeaveConfig.advanceNotice);
        }

        return today;
    }, [selectedLeaveConfig.advanceNotice]);

    const getMaxDate = useCallback((): Date | undefined => {
        if (selectedLeaveType === 'Casual') {
            return undefined; // Allow future dates
        }

        // For other leave types with max duration
        if (selectedLeaveConfig.maxDuration) {
            const maxDate = new Date();
            maxDate.setDate(maxDate.getDate() + selectedLeaveConfig.maxDuration);
            return maxDate;
        }

        // Set maximum date to 1500 days from now
        const maxAllowedDate = new Date();
        maxAllowedDate.setDate(maxAllowedDate.getDate() + MAX_LEAVE_DURATION);
        return maxAllowedDate;
    }, [selectedLeaveType, selectedLeaveConfig.maxDuration]);

    const handleLeaveTypeChange = useCallback((type: LeaveType) => {
        setSelectedLeaveType(type);
        // Reset dates when leave type changes
        setStartDate(null);
        setEndDate(null);
        setDayConfigurations(new Map());
        setReason('');
    }, []);

    const validateForm = useCallback((): string | null => {
        if (!startDate || !endDate) {
            return 'Please select both start and end dates';
        }

        if (endDate < startDate) {
            return 'End date cannot be before start date';
        }

        const today = getTodayMidnight();
        const startDateOnly = normalizeDateToMidnight(startDate);
        const endDateOnly = normalizeDateToMidnight(endDate);

        // For Casual leave, only allow yesterday
        if (selectedLeaveType === 'Casual') {
            const tomorrow = getTomorrowMidnight();

            if (startDateOnly.getTime() < tomorrow.getTime()) {
                return 'Casual leave can only be applied for future dates (starting tomorrow)';
            }
            // Single day restriction removed as per requirement
        } else {
            // For other leave types, don't allow past dates
            if (startDateOnly < today) {
                return 'Cannot apply leave for past dates';
            }

            if (endDateOnly < today) {
                return 'Cannot apply leave for past dates';
            }
        }

        let leaveDuration = 0;

        const current = new Date(startDate);
        const end = new Date(endDate);

        while (current <= end) {
            const dateKey = formatDateToISO(current);
            const config = dayConfigurations.get(dateKey);

            leaveDuration += config?.isHalfDay ? 0.5 : 1;
            current.setDate(current.getDate() + 1);
        }

        // Maximum validation
        if (leaveDuration > MAX_LEAVE_DURATION) {
            return `Sorry, you cannot submit a leave application for more than ${MAX_LEAVE_DURATION} days`;
        }

        // Check minimum duration
        if (
            selectedLeaveType === 'Medical' &&
            selectedLeaveConfig.minDuration &&
            leaveDuration < selectedLeaveConfig.minDuration &&
            leaveDuration !== 0.5
        ) {
            return `Medical leave must be at least ${selectedLeaveConfig.minDuration} day(s)`;
        }


        // Check maximum duration
        if (selectedLeaveConfig.maxDuration && leaveDuration > selectedLeaveConfig.maxDuration) {
            return `${selectedLeaveType} leave cannot exceed ${selectedLeaveConfig.maxDuration} day(s)`;
        }

        if (!reason.trim()) {
            return 'Please provide a reason for leave';
        }

        // if (selectedLeaveConfig.requiresDocumentation && !documentation.trim()) {
        //     return 'Documentation is required for this leave type';
        // }

        if (!companyId || !userId) {
            return 'Missing company or user information';
        }

        // Check leave balance
        if (leaveBalance) {
            const casualAvailable = leaveBalance.casual_leave_balance - leaveBalance.casual_leave_booked;
            const sickAvailable = leaveBalance.sick_leave_balance - leaveBalance.sick_leave_booked;

            if (selectedLeaveType === 'Casual' && leaveDuration > casualAvailable) {
                toast.warn(`Insufficient casual leave balance. Available: ${casualAvailable} days`);
                return `Insufficient casual leave balance. Available: ${casualAvailable} days`;
            }
            if (selectedLeaveType === 'Medical' && leaveDuration > sickAvailable) {
                toast.warn(`Insufficient medical leave balance. Available: ${sickAvailable} days`);
                return `Insufficient medical leave balance. Available: ${sickAvailable} days`;
            }
        }

        return null;
    }, [startDate, endDate, selectedLeaveType, selectedLeaveConfig, reason, documentation, companyId, userId, leaveBalance, dayConfigurations]);

    const handleSubmit = useCallback(async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);

        const validationError = validateForm();
        if (validationError) {
            console.log('Validation Error:', validationError);
            setError(validationError);
            return;
        }

        console.log('Submission started. Date range:', startDate, 'to', endDate);

        let leaveDuration = 0;

        const current = new Date(startDate as any);
        const end = new Date(endDate as any);

        while (current <= end) {
            const dateKey = formatDateToISO(current);
            const config = dayConfigurations.get(dateKey);

            leaveDuration += config?.isHalfDay ? 0.5 : 1;
            current.setDate(current.getDate() + 1);
        }

        // Double-check maximum limit before submission
        if (leaveDuration > MAX_LEAVE_DURATION) {
            setError(`Sorry, you cannot submit a leave application for more than ${MAX_LEAVE_DURATION} days`);
            return;
        }
        if (!userId || !companyId) {
            console.log('User ID or Company ID not found', !userId || !companyId);
            return;
        }

        try {
            setLoading(true);

            // First, get the admin document to find the adminId
            const adminRef = collection(db, "admin");
            const adminQuery = query(
                adminRef,
                where("uid", "==", userId),
                where("company_id", "==", companyId)
            );
            const adminSnapshot = await getDocs(adminQuery);

            if (adminSnapshot.empty) {
                setError('Admin account not found');
                setLoading(false);
                return;
            }

            const adminId = adminSnapshot.docs[0].id;

            // 1. Check if applying for scheduled working days
            console.log('[Leave Debug] Submit check - isStaffAttendanceEnabled:', isStaffAttendanceEnabled, 'workingDays:', workingDays, 'workingDays.length:', workingDays.length);
            if (isStaffAttendanceEnabled && workingDays.length > 0) {
                const daysOfWeek = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
                const currentCheck = new Date(startDate!);
                const lastCheck = new Date(endDate!);

                while (currentCheck <= lastCheck) {
                    const dayName = daysOfWeek[currentCheck.getDay()];
                    console.log('[Leave Debug] Checking day:', dayName, 'included in workingDays:', workingDays.includes(dayName));
                    if (!workingDays.includes(dayName)) {
                        showToast.warn("nonWorkingDay");
                        setLoading(false);
                        return;
                    }
                    currentCheck.setDate(currentCheck.getDate() + 1);
                }
            } else {
                console.log('[Leave Debug] ⚠️ Working day check SKIPPED because:', !isStaffAttendanceEnabled ? 'staff_attendance_enabled is false' : 'workingDays is empty');
            }

            // 2. Check for holidays
            if (holidayDates.length > 0) {
                const currentCheckH = new Date(startDate!);
                const lastCheckH = new Date(endDate!);

                while (currentCheckH <= lastCheckH) {
                    const dateKey = formatDateToISO(currentCheckH);
                    if (holidayDates.includes(dateKey)) {
                        showToast.warn("holidayBlocked");
                        setLoading(false);
                        return;
                    }
                    currentCheckH.setDate(currentCheckH.getDate() + 1);
                }
            }


            // 2. Check for overlapping leave requests
            const leaveRequestsRef = collection(db, 'admin', adminId, 'leaverequests');
            const startTimestamp = new Date(startDate!);
            startTimestamp.setHours(0, 0, 0, 0);
            const endTimestamp = new Date(endDate!);
            endTimestamp.setHours(23, 59, 59, 999);

            const overlapQuery = query(
                leaveRequestsRef,
                where('leave_approved_status', 'in', ['pending', 'approved']),
                where('leave_date', '>=', startTimestamp),
                where('leave_date', '<=', endTimestamp)
            );

            const overlapSnapshot = await getDocs(overlapQuery);
            const overlappingRequest = overlapSnapshot.docs.find(doc => {
                const data = doc.data() as LeaveData;
                const existingDate = data.leave_date?.toDate?.() || new Date(data.leave_date);
                const dateKey = formatDateToISO(existingDate);

                const newDayConfig = dayConfigurations.get(dateKey) || { isHalfDay: false, halfDayType: 'first_half' };

                // Detection based on fh_status / sh_status
                // If existing request has FH, and new request wants FH => collision
                // If existing request has SH, and new request wants SH => collision
                // If new request is full day => collision if existing has FH or SH

                const wantsFH = !newDayConfig.isHalfDay || newDayConfig.halfDayType === 'first_half';
                const wantsSH = !newDayConfig.isHalfDay || newDayConfig.halfDayType === 'second_half';

                const hasFH = !!data.fh_status;
                const hasSH = !!data.sh_status;

                if ((wantsFH && hasFH) || (wantsSH && hasSH)) return true;

                return false;
            });

            if (overlappingRequest) {
                console.log('Overlapping request found:', overlappingRequest.id);
                showToast.warn("leaveOverlapping");
                setLoading(false);
                return;
            }

            // 3. Generate day_details array from dayConfigurations
            const dayDetails: DayDetail[] = [];
            const current = new Date(startDate!);
            const end = new Date(endDate!);
            let totalLeaveDays = 0;

            while (current <= end) {
                const dateKey = formatDateToISO(current);
                const config = dayConfigurations.get(dateKey) || { isHalfDay: false, halfDayType: 'first_half' };

                const dayDetail: DayDetail = {
                    date: dateKey,
                    is_half_day: config.isHalfDay
                };

                if (config.isHalfDay) {
                    dayDetail.half_day_type = config.halfDayType;
                    totalLeaveDays += 0.5;
                } else {
                    totalLeaveDays += 1;
                }

                dayDetails.push(dayDetail);
                current.setDate(current.getDate() + 1);
            }

            // 2. Initial balance check and split calculation
            const year = new Date().getFullYear().toString();
            const leaveBalanceRef = doc(db, 'admin', adminId, 'leavebalance', year);

            let clAvail = 0;
            let slAvail = 0;
            if (leaveBalance) {
                clAvail = Math.max(0, leaveBalance.casual_leave_balance - leaveBalance.casual_leave_booked);
                slAvail = Math.max(0, leaveBalance.sick_leave_balance - leaveBalance.sick_leave_booked);
            }

            let clToBookGlobal = 0;
            let slToBookGlobal = 0;
            let lopToBookGlobal = 0;

            // First pass: Global counts for the system note
            let tempCl = clAvail;
            let tempSl = slAvail;

            dayDetails.forEach(day => {
                const dayWeight = day.is_half_day ? 0.5 : 1.0;
                let needed = dayWeight;

                if (selectedLeaveType === 'Casual') {
                    const take = Math.min(needed, tempCl);
                    clToBookGlobal += take;
                    tempCl -= take;
                    lopToBookGlobal += (needed - take);
                } else if (selectedLeaveType === 'Medical') {
                    const take = Math.min(needed, tempSl);
                    slToBookGlobal += take;
                    tempSl -= take;
                    lopToBookGlobal += (needed - take);
                } else {
                    lopToBookGlobal += needed;
                }
            });

            let finalReason = reason.trim();
            if (lopToBookGlobal > 0 && (selectedLeaveType === 'Casual' || selectedLeaveType === 'Medical')) {
                const primaryCount = selectedLeaveType === 'Casual' ? clToBookGlobal : slToBookGlobal;
                finalReason += ` (System Note: ${primaryCount} ${selectedLeaveType}, ${lopToBookGlobal} LOP due to balance)`;
            }

            // Use batch write
            const batch = writeBatch(db);

            // Second pass: Create separate documents for each day
            let remCl = clAvail;
            let remSl = slAvail;
            const appliedOn = firestoreServerTimestamp();

            for (const day of dayDetails) {
                const dateObj = new Date(day.date);

                let fhStatusVal = "";
                let shStatusVal = "";
                let dayCl = 0;
                let daySl = 0;
                let dayLop = 0;

                const getStatus = (weight: number) => {
                    if (selectedLeaveType === 'Casual') {
                        if (remCl >= weight) {
                            remCl -= weight;
                            dayCl += weight;
                            return `0.5CL`;
                        } else {
                            dayLop += weight;
                            return `0.5LOP`;
                        }
                    } else if (selectedLeaveType === 'Medical') {
                        if (remSl >= weight) {
                            remSl -= weight;
                            daySl += weight;
                            return `0.5SL`;
                        } else {
                            dayLop += weight;
                            return `0.5LOP`;
                        }
                    } else {
                        dayLop += weight;
                        return `0.5LOP`;
                    }
                };

                if (!day.is_half_day) {
                    fhStatusVal = getStatus(0.5);
                    shStatusVal = getStatus(0.5);
                } else {
                    const status = getStatus(0.5);
                    if (day.half_day_type === 'first_half') {
                        fhStatusVal = status;
                        shStatusVal = "";
                    } else {
                        fhStatusVal = "";
                        shStatusVal = status;
                    }
                }

                const singleDayData: LeaveData = {
                    admin_id: adminId,
                    company_id: companyId!,
                    leave_type: selectedLeaveConfig.code,
                    reason: finalReason,
                    leave_approved_status: 'pending',
                    applied_on: appliedOn,
                    created_at: appliedOn,
                    leave_date: dateObj,
                    month_num: dateObj.getMonth() + 1,
                    year_num: dateObj.getFullYear(),
                    fh_status: fhStatusVal,
                    sh_status: shStatusVal
                };

                const leaveRef = doc(collection(db, 'admin', adminId, 'leaverequests'));
                batch.set(leaveRef, singleDayData);
            }

            // 3. Global Balance Update
            const balanceUpdates: any = {};
            if (clToBookGlobal > 0) balanceUpdates.casual_leave_booked = increment(clToBookGlobal);
            if (slToBookGlobal > 0) balanceUpdates.sick_leave_booked = increment(slToBookGlobal);
            if (lopToBookGlobal > 0) balanceUpdates.lop_leave_booked = increment(lopToBookGlobal);

            if (Object.keys(balanceUpdates).length > 0) {
                batch.update(leaveBalanceRef, balanceUpdates);
            }

            console.log('Committing batch write...');
            await batch.commit();
            console.log('Batch commit successful');

            showToast.success("leaveAdded");

            handleCloseModal();
            await fetchLeaveBalance();
            await fetchLeaveRecords();
        } catch (err) {
            console.error('Error submitting leave application:', err);
            // debugger; // Pause here to inspect the error
            setError('Failed to submit leave application. Please try again.');
        } finally {
            setLoading(false);
        }
    }, [validateForm, startDate, endDate, userId, userName, selectedLeaveType, selectedLeaveConfig, reason, remarks, documentation, companyId, handleCloseModal, fetchLeaveBalance, fetchLeaveRecords, dayConfigurations, leaveBalance, isStaffAttendanceEnabled, workingDays, holidayDates]);




    // Memoized filtered leave records
    const { upcomingLeaves, previousLeaves } = useMemo(() => {
        const today = getTodayMidnight();

        const upcoming = leaveRecords.filter(leave => {
            const date = leave.leave_date?.toDate?.() || new Date(leave.leave_date);
            return date >= today && leave.leave_approved_status !== 'rejected';
        });

        const previous = leaveRecords.filter(leave => {
            const date = leave.leave_date?.toDate?.() || new Date(leave.leave_date);
            return date < today || leave.leave_approved_status === 'rejected';
        });

        return { upcomingLeaves: upcoming, previousLeaves: previous };
    }, [leaveRecords]);

    // Memoized leave balances
    const leaveBalances = useMemo(() => {
        if (!leaveBalance) {
            return {
                casualAvailable: 0,
                casualBooked: 0,
                lopAvailable: 0,
                lopBooked: 0,
                sickAvailable: 0,
                sickBooked: 0,
            };
        }

        return {
            casualAvailable: leaveBalance.casual_leave_balance - leaveBalance.casual_leave_booked,
            casualBooked: leaveBalance.casual_leave_booked,
            lopAvailable: leaveBalance.lop_leave_balance - leaveBalance.lop_leave_booked,
            lopBooked: leaveBalance.lop_leave_booked,
            sickAvailable: leaveBalance.sick_leave_balance - leaveBalance.sick_leave_booked,
            sickBooked: leaveBalance.sick_leave_booked,
        };
    }, [leaveBalance]);

    return (
        <div className="page">
            <Container size="xl" > <Box
                        sx={(theme) => ({
                          position: "sticky",
                          top: 110, // Avoid overlapping the fixed navbar
                          zIndex: 100,
                          backgroundColor: "#FAFAFA",
                          paddingTop: theme.spacing.sm,
                          //paddingBottom: theme.spacing.xs,
                          marginTop: `-${theme.spacing.sm}`,
                        })}
                      >
                <SimpleGrid cols={2}>
                    <BenchSettingsBreadcrumbs links={breadcrumbLinks} />
                    <Group position="right">
                        <PermissionButton
                            label="Leave Approvals"
                            variant="light"
                            onClick={() => navigate(ROUTES.LEAVE_APPROVALS)}
                            resourceId={'leave'} actionType={'update'}                        >

                        </PermissionButton>
                        <Button
                            onClick={() => handleOpenModal()}
                        >
                            Apply Leave
                        </Button>
                    </Group>
                </SimpleGrid>
                <Divider my="sm" /> </Box>
                
                
                <Stack spacing="lg">
                    {/* Breadcrumb */}


                    {/* Header */}


                    {/* Leave Summary Cards */}
                    <SimpleGrid cols={3} breakpoints={[
                        { maxWidth: 'md', cols: 2 },
                        { maxWidth: 'sm', cols: 1 }
                    ]}>
                        <LeaveSummaryCard
                            title="Casual"
                            icon={<IconBeach />}
                            iconColor="#228be6"
                            iconClassName={classes.casualIcon}
                            available={leaveBalances.casualAvailable}
                            booked={leaveBalances.casualBooked}
                            onApply={() => handleOpenModal('Casual')}
                        />
                        <LeaveSummaryCard
                            title="Medical"
                            icon={<IconFirstAidKit />}
                            iconColor="#12b886"
                            iconClassName={classes.sickIcon}
                            available={leaveBalances.sickAvailable}
                            booked={leaveBalances.sickBooked}
                            onApply={() => handleOpenModal('Medical')}
                        />
                        <LeaveSummaryCard
                            title="LOP"
                            icon={<IconCurrencyDollarOff />}
                            iconColor="#fa5252"
                            iconClassName={classes.lopIcon}
                            available={leaveBalances.lopAvailable}
                            booked={leaveBalances.lopBooked}
                            availableColor="red"
                            onApply={() => handleOpenModal('LOP')}
                        />
                    </SimpleGrid>



                    {/* Leave Records Table */}
                    <BenchPaper title={'Leave Records'}>
                        <LeaveTable
                            leaves={leaveRecords}
                            emptyMessage="No leave records found"
                        />
                        {/* Pagination Section */}

                    </BenchPaper>   <div
                        style={{
                            marginTop: "1rem",
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            padding: "0 1rem 1rem 1rem"
                        }}
                    >
                        <div style={{ display: "flex", alignItems: "center" }}>
                            <Text>Rows per page : </Text>
                            <Select
                                style={{ width: "100px", marginLeft: "5px" }}
                                value={itemsPerPage}
                                onChange={(val) => {
                                    setItemsPerPage(val || "10");
                                    fetchLeaveRecords("first", val || "10");
                                }}
                                data={[
                                    { value: "10", label: "10" },
                                    { value: "25", label: "25" },
                                    { value: "50", label: "50" },
                                ]}
                            />
                        </div>
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "1rem",
                            }}
                        >
                            {/* <Text>Total: {totalResultCount}</Text> */}
                            <Group>
                                <Button
                                    leftIcon={<IconArrowLeft size={14} />}
                                    disabled={currentPage === 1}
                                    onClick={() => fetchLeaveRecords("prev")}
                                >
                                    Prev
                                </Button>
                                <Text>{currentPage}</Text>
                                <Button
                                    rightIcon={<IconArrowRight size={14} />}
                                    onClick={() => fetchLeaveRecords("next")}
                                    disabled={!hasNextPage}
                                >
                                    Next
                                </Button>
                            </Group>
                        </div>
                    </div>
                </Stack>
            </Container>

            <Modal
                opened={modalOpen}
                onClose={handleCloseModal}
                withCloseButton={false}
                padding="xl"
                size="lg"
                radius="lg"
            >
                <div className={classes.modalHeader}>
                    <Text size="xl" weight={700}>Apply {selectedLeaveConfig.label} Leave</Text>
                    <ActionIcon
                        className={classes.closeButton}
                        onClick={handleCloseModal}
                        radius="md"
                        size="lg"
                    >
                        <IconX size={20} />
                    </ActionIcon>
                </div>

                <form onSubmit={handleSubmit}>
                    <Stack spacing="lg">
                        {error && (
                            <Alert icon={<IconAlertCircle size={16} />} color="red" radius="md">
                                {error}
                            </Alert>
                        )}

                        {/* Leave Type Selector - Styled like SegmentedControl */}
                        <div>
                            <Text size="sm" weight={600} mb="xs" color="dimmed">Leave Type</Text>
                            <div className={classes.segmentedControl}>
                                {LEAVE_TYPES_CONFIG.map((config) => (
                                    <div
                                        key={config.type}
                                        className={classes.segmentedButton}
                                        data-active={selectedLeaveType === config.type}
                                        onClick={() => handleLeaveTypeChange(config.type)}
                                    >
                                        {config.type}
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Information Box */}
                        <div className={classes.infoBox} style={{ marginTop: '10px' }}>
                            <Group spacing="sm" align="flex-start" noWrap>
                                <ThemeIcon variant="light" color="red" radius="xl" size="sm">
                                    <IconInfoCircle size={14} />
                                </ThemeIcon>
                                <Stack spacing={4}>
                                    <Text size="sm" weight={700} color="red.7">
                                        {selectedLeaveConfig.label} Leave Information
                                    </Text>
                                    <Text size="xs" color="dimmed">
                                        {selectedLeaveType === 'Casual' && 'Casual leave is for personal matters.'}
                                        {selectedLeaveType === 'Medical' && 'Medical leave is for health-related absences.'}
                                        {selectedLeaveType === 'LOP' && 'LOP leave is for absences without pay.'}
                                    </Text>
                                    {selectedLeaveType === 'Casual' && (
                                        <Text size="xs" weight={700} color="orange.8">
                                            Note: Please provide at least 48 hours advance notice for planned leaves.
                                        </Text>
                                    )}
                                    {selectedLeaveType === 'Medical' && (
                                        <Text size="xs" weight={700} color="orange.8">
                                            Note: Medical certificate may be required for leaves exceeding 3 days.
                                        </Text>
                                    )}
                                </Stack>
                            </Group>
                        </div>

                        {/* Date Inputs */}
                        <SimpleGrid cols={2} spacing="md" mt="lg">
                            <DatePickerInput
                                aria-placeholder="Select Start Date"
                                label="Start Date"
                                // label={
                                //     <div className={classes.labelWithIcon}>
                                //         <Text size="sm" weight={500}>Start Date</Text>
                                //         <IconQuestionMark size={14} color="gray" />
                                //     </div>
                                // }
                                value={startDate}
                                onChange={setStartDate}
                                required
                                minDate={selectedLeaveType === 'Casual' ? getTomorrowMidnight() : getMinDate()}
                                rightSection={<IconCalendar size={18} color="gray" />}
                                radius="md"
                                styles={{ input: startDate ? { border: '1px solid #ee374d' } : {} }}
                            />

                            <DatePickerInput
                                label="End Date"
                                // label={
                                //     <div className={classes.labelWithIcon}>
                                //         <Text size="sm" weight={500}>End Date</Text>

                                //         <IconQuestionMark size={14} color="gray" />
                                //     </div>
                                // }
                                value={endDate}
                                onChange={setEndDate}
                                required
                                minDate={startDate || (selectedLeaveType === 'Casual' ? getTomorrowMidnight() : getMinDate())}
                                rightSection={<IconCalendar size={18} color="gray" />}
                                radius="md"
                            />
                        </SimpleGrid>

                        {/* Day Details for Half Day Selection */}
                        {startDate && endDate && (
                            <Stack spacing="xs">
                                <Text size="sm" weight={600} color="dimmed" mt="xs">Select Leave Duration for each day:</Text>
                                <Paper withBorder p="xs" radius="md">
                                    <Stack spacing="xs">
                                        {Array.from({ length: calculateDuration(startDate, endDate) }).map((_, idx) => {
                                            const date = new Date(startDate);
                                            date.setDate(date.getDate() + idx);
                                            const dateKey = formatDateToISO(date);
                                            const config = dayConfigurations.get(dateKey) || { isHalfDay: false, halfDayType: 'first_half' };

                                            return (
                                                <Group key={dateKey} position="apart" noWrap>
                                                    <Text size="sm" weight={500}>{formatDate(date.toISOString())}</Text>
                                                    <SegmentedControl
                                                        size="xs"
                                                        value={!config.isHalfDay ? 'full' : config.halfDayType}
                                                        onChange={(value) => {
                                                            const newMap = new Map(dayConfigurations);
                                                            if (value === 'full') {
                                                                newMap.set(dateKey, { isHalfDay: false, halfDayType: 'first_half' });
                                                            } else {
                                                                newMap.set(dateKey, { isHalfDay: true, halfDayType: value as HalfDayType });
                                                            }
                                                            setDayConfigurations(newMap);
                                                        }}
                                                        data={[
                                                            { label: 'Full Day', value: 'full' },
                                                            { label: 'First Half', value: 'first_half' },
                                                            { label: 'Second Half', value: 'second_half' },
                                                        ]}
                                                        styles={{
                                                            root: { backgroundColor: '#f8f9fa' },
                                                            indicator: { backgroundColor: '#ee374d' },
                                                            label: {
                                                                '&[data-active]': { color: 'white' }
                                                            }
                                                        }}
                                                    />
                                                </Group>
                                            );
                                        })}
                                    </Stack>
                                </Paper>
                                <Group position="right">
                                    <Badge color="red" variant="dot" size="lg" radius="sm">
                                        Total Duration: {duration} {duration === 1 ? 'Day' : 'Days'}
                                    </Badge>
                                </Group>
                            </Stack>
                        )}

                        {/* Reason and Remarks */}
                        <Textarea
                            label="Reason"
                            placeholder="Please describe why you need this leave..."
                            value={reason}
                            onChange={(e) => setReason(e.currentTarget.value)}
                            required
                            minRows={3}
                            radius="md"
                        />

                        <Textarea
                            label="Remarks (Options)"
                            placeholder="Any additional notes for your manager..."
                            value={remarks}
                            onChange={(e) => setRemarks(e.currentTarget.value)}
                            minRows={2}
                            radius="md"
                        />

                        {/* Footer Buttons */}
                        <Group position="right" mt="xl">
                            <Button
                                className={classes.cancelButton}
                                onClick={handleCloseModal}
                                disabled={loading}
                                size="sm"
                                radius="md"
                            >
                                Cancel
                            </Button>
                            <Button
                                className={classes.submitButton}
                                type="submit"
                                loading={loading}
                                size="sm"
                                radius="md"
                            >
                                {loading ? 'Submitting...' : 'Submit Application'}
                            </Button>
                        </Group>
                    </Stack>
                </form>
            </Modal>

            <BenchLoader isLoading={initialLoading} />
        </div>
    );
}